import React, { Suspense, lazy } from "react";
import { useTranslation } from "react-i18next";
import Sidebar from "./Sidebar";
import SupplierRouteGuard from "./SupplierRouteGuard";
import RequireFeatureFlag from "./common/RequireFeatureFlag";
import Box from "@mui/material/Box";
import { Navigate, Route, Routes } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import {
  DASHBOARD_ROUTE,
  INVOICEPREVIEW_ROUTE,
  INVOICE_PREVIEW_ROUTE,
  INVOICE_ROUTE,
  METRICSINVOICE_ROUTE,
  MYTEAM_ROUTE,
  SUPPLIER_SITE_ROUTE,
  SETTINGS_ROUTE,
  SUPPLIERMANAGEMENT_ROUTE,
  PROFILE_ROUTE,
  MYCLIENTS_ROUTE,
  ADD_SUPPLIER,
  NOTIFICATION_ROUTE,
  INBOX_ROUTE,
  EXPENSES_ROUTE,
  EXPENSES_MINE_ROUTE,
  EXPENSES_PENDING_ROUTE,
  EXPENSES_SUBMIT_ROUTE,
  EXPENSES_DETAIL_ROUTE,
} from "../constants/RoutePaths";

// Lazy load all page components for code splitting
const Dashboard = lazy(() => import("./Dashboard"));
const Invoice = lazy(() => import("./Invoice"));
const InvoicePreview = lazy(() => import("./InvoicePreview"));
const InvoiceSinglePreview = lazy(() => import("./InvoiceSinglePreview"));
const MyTeam = lazy(() => import("./MyTeam"));
const SupplierManagement = lazy(() => import("./SupplierManagement"));
const Settings = lazy(() => import("./Administration"));
const MetricsInvoice = lazy(() => import("./MetricsInvoice"));
const Profile = lazy(() => import("./Profile"));
const MyClients = lazy(() => import("./MyClients"));
const AddSupplier = lazy(() => import("./AddSupplier"));
const NotificationList = lazy(() => import("./Notification/NotificationList"));
const SupplierDetais = lazy(() => import("./SupplierDetails"));
const Inbox = lazy(() => import("./Inbox/Inbox"));
const MyExpenses = lazy(() => import("./Expense/MyExpenses"));
const PendingApprovals = lazy(() => import("./Expense/PendingApprovals"));
const ExpenseSubmit = lazy(() => import("./Expense/ExpenseSubmit"));
const ExpenseDetail = lazy(() => import("./Expense/ExpenseDetail"));

// Loading fallback component
const PageLoader = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "400px",
    }}
  >
    <CircularProgress sx={{ color: "#FC6634" }} />
  </Box>
);

export default function Layouts() {
  const { t: _t } = useTranslation("common");
  return (
    <>
      <Box sx={{ display: "flex" }}>
        <Sidebar />
        <Box component="main" sx={{ flexGrow: 1, p: 3, pt: 2, minWidth: 0, overflow: "hidden" }}>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route
                path={DASHBOARD_ROUTE}
                element={
                  <SupplierRouteGuard>
                    <Dashboard />
                  </SupplierRouteGuard>
                }
              ></Route>
              <Route path={NOTIFICATION_ROUTE} element={<NotificationList />}></Route>
              <Route
                path={INVOICE_ROUTE}
                element={
                  <SupplierRouteGuard>
                    <Invoice />
                  </SupplierRouteGuard>
                }
              ></Route>
              <Route path={SUPPLIERMANAGEMENT_ROUTE} element={<SupplierManagement />}></Route>
              <Route path={MYCLIENTS_ROUTE} element={<MyClients />}></Route>
              <Route path={ADD_SUPPLIER} element={<AddSupplier />}></Route>
              <Route path={SETTINGS_ROUTE} element={<Settings />}></Route>
              <Route path={METRICSINVOICE_ROUTE} element={<MetricsInvoice />}></Route>
              <Route
                path={`${INVOICEPREVIEW_ROUTE}/:${"invoiceID"}`}
                element={<InvoicePreview />}
              ></Route>
              <Route
                path={`${INVOICE_PREVIEW_ROUTE}/:${"invoiceID"}`}
                element={<InvoiceSinglePreview />}
              ></Route>
              <Route
                path={"" + INVOICEPREVIEW_ROUTE + "/:invoiceID"}
                element={<InvoicePreview />}
              ></Route>
              <Route
                path={"" + SUPPLIER_SITE_ROUTE + "/:supplierID"}
                element={<SupplierDetais />}
              ></Route>
              <Route path={INBOX_ROUTE} element={<Inbox />}></Route>
              <Route path={PROFILE_ROUTE} element={<Profile />}></Route>
              <Route path={MYTEAM_ROUTE} element={<MyTeam />}></Route>
              {/* Expense Module — gated on expenseModuleEnabled feature flag */}
              <Route
                path={EXPENSES_ROUTE}
                element={<Navigate to={EXPENSES_MINE_ROUTE} replace />}
              ></Route>
              <Route
                path={EXPENSES_MINE_ROUTE}
                element={
                  <RequireFeatureFlag flag="expenseModuleEnabled">
                    <SupplierRouteGuard>
                      <MyExpenses />
                    </SupplierRouteGuard>
                  </RequireFeatureFlag>
                }
              ></Route>
              <Route
                path={EXPENSES_PENDING_ROUTE}
                element={
                  <RequireFeatureFlag flag="expenseModuleEnabled">
                    <SupplierRouteGuard>
                      <PendingApprovals />
                    </SupplierRouteGuard>
                  </RequireFeatureFlag>
                }
              ></Route>
              <Route
                path={EXPENSES_SUBMIT_ROUTE}
                element={
                  <RequireFeatureFlag flag="expenseModuleEnabled">
                    <SupplierRouteGuard>
                      <ExpenseSubmit />
                    </SupplierRouteGuard>
                  </RequireFeatureFlag>
                }
              ></Route>
              <Route
                path={EXPENSES_DETAIL_ROUTE}
                element={
                  <RequireFeatureFlag flag="expenseModuleEnabled">
                    <SupplierRouteGuard>
                      <ExpenseDetail />
                    </SupplierRouteGuard>
                  </RequireFeatureFlag>
                }
              ></Route>
              {/* <Route path={AUTHENTIFICATION_ROUTE} element={<Authentification />}></Route> */}
              <Route path={"*"} element={<Navigate to={"pagenotfound"} />}></Route>
            </Routes>
          </Suspense>
        </Box>
      </Box>
    </>
  );
}
